
import { useNavigate } from '@remix-run/react';
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User } from '~/models/User';
import { createUser, getClient } from '~/module/auth/auth';

interface AuthContextType {
  user: User | null;
  error: unknown | null;
  setUser: (user: User | null) => void;
  setError: (error: unknown | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<unknown | null>(null);

  return (
    <AuthContext.Provider value={{ user, error, setUser, setError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export function authGuard() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const auth = getClient();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('auth state changed', user);
      if (user) {
        const newUser = await createUser(user);
        setUser(newUser);
        return
      }
      console.log('redirecting to login', window.location.href);
      navigate({
        pathname: "/",
        search: `?redirect_to=${window.location.href}`,
      }, { replace: true });
    });
    return () => {
      unsubscribe();
    };
  }, []);
}
